/**
 * CORE-1102 — one unread poll for the whole app.
 *
 * `useChatApiUnreadCounts` used to own everything itself: a 30 s interval, a
 * `focus` listener, a `visibilitychange` listener, an `onUnreadChanged`
 * subscription and a `MessageNotify` listener — all created PER CALLER. There
 * are five callers (header badge, bottom tabs, the Agapella widget, the widget's
 * native twin, the messages list), so a desktop page with the header and the
 * widget up ran two of everything. Measured on dev: two `unread-summary` GETs
 * every 30 s, 1 s apart — the offset being simply when each component mounted.
 * Both requests return the same number to the same screen.
 *
 * So the counts move here, behind a subscription: one poll, one listener set,
 * one in-flight request, however many badges render it. A caller that mounts
 * second gets the number already in hand — synchronously, with no request at
 * all — which also removes the badge's mount-time flash of zero.
 *
 * The poll itself is visibility-aware (see {@link startVisibilityAwareInterval}):
 * hidden tabs stop polling and catch up when they come back.
 *
 * Kept as a plain object with injectable deps — like `createReadPointer` and
 * `createUnreadRefresher` next door — so it is testable without a React
 * renderer or a DOM.
 */
import { startVisibilityAwareInterval, type VisibilityDocument } from '../../utils/visibilityAwareInterval';
import { createUnreadRefresher } from './unreadRefresher';

export interface ChatApiUnreadCounts {
  /** Unread across every channel the caller belongs to (DM + AI + circle). */
  totalUnread: number;
  /** Number of channels with at least one unread message. */
  unreadChannels: number;
  /** Unread in human 1:1 DMs only — the global-header chat badge (CORE-67). */
  friendsUnread: number;
  /** Unread across the «Сообщения» list scope (DM + AI, not circles) — bottom-tab badge. */
  conversationsUnread: number;
  /** Unread from AI companions only — the Agapella launcher badge (CORE-499). */
  aiUnread: number;
}

/** Совпадают ли все счётчики — тогда подписчикам рассылать нечего. */
export function sameUnreadCounts(a: ChatApiUnreadCounts, b: ChatApiUnreadCounts): boolean {
  return a.totalUnread === b.totalUnread
    && a.unreadChannels === b.unreadChannels
    && a.friendsUnread === b.friendsUnread
    && a.conversationsUnread === b.conversationsUnread
    && a.aiUnread === b.aiUnread;
}

export const ZERO_UNREAD: ChatApiUnreadCounts = {
  totalUnread: 0,
  unreadChannels: 0,
  friendsUnread: 0,
  conversationsUnread: 0,
  aiUnread: 0,
};

/** Re-poll cadence — the dropped-event safety net, unchanged from CORE-617. */
export const REFRESH_MS = 30_000;

export interface UnreadStoreDeps {
  /** Reads the authoritative counts. */
  fetchCounts: () => Promise<ChatApiUnreadCounts>;
  /** Local «a thread moved its read pointer» signal. Returns an unsubscribe. */
  onUnreadChanged: (listener: () => void) => () => void;
  /** Per-user `MessageNotify` fan-out. Returns an unsubscribe. */
  onMessage: (listener: () => void) => () => void;
  /**
   * CORE-1753 — the `MessageNotify` socket (re)connected: whatever was sent
   * while it was down never arrives. Returns an unsubscribe.
   */
  onLiveFeedUp?: (listener: () => void) => () => void;
  /** Web `focus`. Returns an unsubscribe; omit off web. */
  onFocus?: (listener: () => void) => () => void;
  intervalMs?: number;
  /** Injected in tests; `null` forces the no-visibility-signal path. */
  doc?: VisibilityDocument | null;
}

export interface UnreadStore {
  /**
   * Registers a badge. Calls `listener` on every change and returns an
   * unsubscribe. The first subscriber starts the poll; the last one to leave
   * stops it and drops every listener.
   */
  subscribe(listener: (counts: ChatApiUnreadCounts) => void): () => void;
  /** The counts as last read — what a newly mounted badge renders immediately. */
  getCounts(): ChatApiUnreadCounts;
  /** Re-read now (auth just changed, a thread was read, …). */
  refresh(): void;
  /** Signed out: blank the counts and tell every badge. */
  reset(): void;
}

export function createUnreadStore(deps: UnreadStoreDeps): UnreadStore {
  const { fetchCounts, onUnreadChanged, onMessage, onLiveFeedUp, onFocus, intervalMs = REFRESH_MS, doc } = deps;

  const listeners = new Set<(counts: ChatApiUnreadCounts) => void>();
  let counts: ChatApiUnreadCounts = ZERO_UNREAD;
  const teardown: Array<() => void> = [];
  /**
   * Collapses concurrent reads. Without it the first paint fires one GET per
   * badge: they all mount in the same tick, before any response lands.
   */
  let inFlight: Promise<void> | null = null;
  /** CORE-1753 — a read was asked for after the one in flight had already left. */
  let rereadAfterFlight = false;

  const emit = () => {
    for (const l of [...listeners]) l(counts);
  };

  const refresh = () => {
    if (inFlight) return;
    inFlight = fetchCounts()
      .then((next) => {
        // CORE-1792 — те же числа не рассылаются: новый объект с прежними
        // значениями перерисовывал всех подписчиков, включая скрытые под
        // перепиской «Главную» и список (на Android раз в ~3 с по 70–110 мс,
        // прокрутка переписки дёргалась). Бейджи показывают то же самое.
        if (sameUnreadCounts(counts, next)) return;
        counts = next;
        emit();
      })
      // Best-effort by design (CORE-617): a chat hiccup must never throw into a
      // badge. Keeping the previous numbers beats blanking them on one 500.
      .catch(() => { /* noop */ })
      .finally(() => {
        inFlight = null;
        if (rereadAfterFlight) {
          rereadAfterFlight = false;
          refresh();
        }
      });
  };

  /**
   * CORE-1753 — a read that must start AFTER now. `refresh` folds into the read
   * in flight, which is right for badges mounting together, but not here: on
   * DEV the first read left at ~1.8 s, before Agapella's greeting existed, and
   * its answer is exactly the stale zero this read is meant to replace.
   */
  const refreshAfterNow = () => {
    // Hidden tab: same rule as the poll — nobody sees the badge, and coming
    // back into view reads the counts anyway.
    const target = doc !== undefined ? doc : typeof document === 'undefined' ? null : (document as unknown as VisibilityDocument);
    if (target?.hidden) return;
    if (inFlight) {
      rereadAfterFlight = true;
      return;
    }
    refresh();
  };

  const start = () => {
    const refresher = createUnreadRefresher({ refresh });
    teardown.push(
      startVisibilityAwareInterval({ onTick: refresh, intervalMs, doc }),
      onUnreadChanged(refresh),
      onMessage(() => refresher.bump()),
      () => refresher.cancel(),
    );
    // CORE-1753, возврат QA 23.10 — без этого отметка о приветствии при входе
    // появлялась через ~30 с: `MessageNotify` пришёл раньше, чем открылся
    // сокет, а первый запрос ушёл раньше, чем появилось приветствие. Сразу,
    // без секунды `refresher`: при входе ждать указатель прочтения незачем, а
    // секунда сверху — это уже 3,3 с вместо 2,3. Цена — на переподключении с
    // открытой беседой, куда за время обрыва что-то пришло, бейдж может
    // мелькнуть до того, как беседа отметит прочитанное.
    if (onLiveFeedUp) teardown.push(onLiveFeedUp(refreshAfterNow));
    // No leading `refresh()` here: `startVisibilityAwareInterval` opens with a
    // tick when the page is visible, and deliberately stays silent when it is
    // not — a badge nobody can see is not worth a request.
    if (onFocus) teardown.push(onFocus(refresh));
  };

  const stop = () => {
    for (const t of teardown.splice(0)) t();
    rereadAfterFlight = false;
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      if (listeners.size === 1) start();
      let live = true;
      return () => {
        if (!live) return;
        live = false;
        listeners.delete(listener);
        if (listeners.size === 0) stop();
      };
    },
    getCounts: () => counts,
    refresh,
    reset() {
      counts = ZERO_UNREAD;
      emit();
    },
  };
}
